import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

const defaultTimeSlots = [
  { label: "09:00 AM", startHour: 9, startMin: 0, endHour: 9, endMin: 30 },
  { label: "09:30 AM", startHour: 9, startMin: 30, endHour: 10, endMin: 0 },
  { label: "10:00 AM", startHour: 10, startMin: 0, endHour: 10, endMin: 30 },
  { label: "10:30 AM", startHour: 10, startMin: 30, endHour: 11, endMin: 0 },
  { label: "11:00 AM", startHour: 11, startMin: 0, endHour: 11, endMin: 30 },
  { label: "11:30 AM", startHour: 11, startMin: 30, endHour: 12, endMin: 0 },
  { label: "01:00 PM", startHour: 13, startMin: 0, endHour: 13, endMin: 30 },
  { label: "01:30 PM", startHour: 13, startMin: 30, endHour: 14, endMin: 0 },
  { label: "02:00 PM", startHour: 14, startMin: 0, endHour: 14, endMin: 30 },
  { label: "02:30 PM", startHour: 14, startMin: 30, endHour: 15, endMin: 0 },
  { label: "03:00 PM", startHour: 15, startMin: 0, endHour: 15, endMin: 30 },
  { label: "03:30 PM", startHour: 15, startMin: 30, endHour: 16, endMin: 0 },
  { label: "04:00 PM", startHour: 16, startMin: 0, endHour: 16, endMin: 30 },
  { label: "04:30 PM", startHour: 16, startMin: 30, endHour: 17, endMin: 0 },
];

const AddSechduleTime = ({ secShowHide, handleSecShowHide, handleSubmitSchedule }) => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  const [customStart, setCustomStart] = useState("09:00");
  const [customEnd, setCustomEnd] = useState("09:30");
  const [customError, setCustomError] = useState("");

  // Reset state when the modal is opened
  useEffect(() => {
    if (secShowHide) {
      setEvents([]);
      setSelectedDate(new Date());
      setCustomStart("09:00");
      setCustomEnd("09:30");
      setCustomError("");
    }
  }, [secShowHide]);

  const getStartOfDay = (date) => {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day;
  };

  const isPastDate = (date) => getStartOfDay(date).getTime() < getStartOfDay(new Date()).getTime();

  const getSlotDateTime = (slot) => {
    const slotDate = new Date(selectedDate);
    slotDate.setHours(slot.startHour, slot.startMin, 0, 0);
    return slotDate;
  };

  const isPastSlot = (slot) => getSlotDateTime(slot).getTime() <= new Date().getTime();

  const getCurrentTimeValue = () => moment().format("HH:mm");

  const handleSelectDate = (obj) => {
    if (isPastDate(obj.start)) {
      setCustomError("Past dates are not available for scheduling.");
      return;
    }

    setCustomError("");
    setSelectedDate(obj.start);
  };

  const handleDateChanged = (date) => {
    const nextDate = isPastDate(date) ? new Date() : date;
    setSelectedDate(nextDate);
  };

  const isSelected = (slot) => {
    return events.some((e) => {
      const eStart = new Date(e.start);
      return (
        eStart.getFullYear() === selectedDate.getFullYear() &&
        eStart.getMonth() === selectedDate.getMonth() &&
        eStart.getDate() === selectedDate.getDate() &&
        eStart.getHours() === slot.startHour &&
        eStart.getMinutes() === slot.startMin
      );
    });
  };

  const handleToggleSlot = (slot) => {
    if (isPastDate(selectedDate) || isPastSlot(slot)) {
      setCustomError("Past time slots are not available for scheduling.");
      return;
    }

    setCustomError("");
    const isAlreadySelected = isSelected(slot);
    if (isAlreadySelected) {
      const updated = events.filter((e) => {
        const eStart = new Date(e.start);
        const matches = (
          eStart.getFullYear() === selectedDate.getFullYear() &&
          eStart.getMonth() === selectedDate.getMonth() &&
          eStart.getDate() === selectedDate.getDate() &&
          eStart.getHours() === slot.startHour &&
          eStart.getMinutes() === slot.startMin
        );
        return !matches;
      });
      setEvents(updated);
    } else {
      const startDt = new Date(selectedDate);
      startDt.setHours(slot.startHour, slot.startMin, 0, 0);

      const endDt = new Date(selectedDate);
      endDt.setHours(slot.endHour, slot.endMin, 0, 0);

      let lastId = 1;
      if (events.length > 0) {
        lastId = events[events.length - 1].optionId + 1;
      }

      const newEvent = {
        optionId: lastId,
        start: startDt,
        end: endDt,
        title: "Meeting",
        slots: [startDt, endDt],
      };
      setEvents([...events, newEvent]);
    }
  };

  const handleAddCustomSlot = () => {
    setCustomError("");
    if (!customStart || !customEnd) {
      setCustomError("Please pick start and end times.");
      return;
    }
    const [sHour, sMin] = customStart.split(":").map(Number);
    const [eHour, eMin] = customEnd.split(":").map(Number);

    if (sHour > eHour || (sHour === eHour && sMin >= eMin)) {
      setCustomError("Start time must be before end time.");
      return;
    }

    const startDt = new Date(selectedDate);
    startDt.setHours(sHour, sMin, 0, 0);

    const endDt = new Date(selectedDate);
    endDt.setHours(eHour, eMin, 0, 0);

    if (isPastDate(selectedDate) || startDt.getTime() <= new Date().getTime()) {
      setCustomError("Start time must be in the future.");
      return;
    }

    // Prevent duplicates
    const isDuplicate = events.some((e) => {
      return (
        e.start.getTime() === startDt.getTime() &&
        e.end.getTime() === endDt.getTime()
      );
    });

    if (isDuplicate) {
      setCustomError("This time slot is already added.");
      return;
    }

    let lastId = 1;
    if (events.length > 0) {
      lastId = events[events.length - 1].optionId + 1;
    }

    const newEvent = {
      optionId: lastId,
      start: startDt,
      end: endDt,
      title: "Meeting",
      slots: [startDt, endDt],
    };
    setEvents([...events, newEvent]);
  };

  const getSelectedSlotsForDate = () => {
    return events.filter((e) => {
      const eStart = new Date(e.start);
      return (
        eStart.getFullYear() === selectedDate.getFullYear() &&
        eStart.getMonth() === selectedDate.getMonth() &&
        eStart.getDate() === selectedDate.getDate()
      );
    }).sort((a, b) => a.start.getTime() - b.start.getTime());
  };

  const formatTime = (dateObj) => {
    return new Date(dateObj).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const doSubmit = () => {
    const upcomingEvents = events.filter((event) => new Date(event.start).getTime() > new Date().getTime());
    if (upcomingEvents.length !== events.length) {
      setEvents(upcomingEvents);
    }

    handleSubmitSchedule(upcomingEvents);
  };

  const localizer = momentLocalizer(moment);
  const formattedSelectedDate = moment(selectedDate).format("dddd, MMMM DD");

  return (
    <>
      <Modal
        show={secShowHide}
        size="lg"
        onHide={handleSecShowHide}
        centered
        scrollable
        dialogClassName="rt-add-schedule-modal"
        backdropClassName="rt-add-schedule-backdrop"
        style={{ zIndex: 1110 }}
      >
        <style>{`
          .modal:has(.rt-add-schedule-modal) {
            z-index: 1110 !important;
          }
          .modal-dialog-centered.modal-dialog-scrollable.rt-add-schedule-modal {
            height: auto !important;
          }
          .rt-add-schedule-modal .modal-content {
            max-height: calc(100vh - 60px) !important;
            height: auto !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .rt-add-schedule-backdrop {
            z-index: 1100 !important;
            backdrop-filter: blur(4px) !important;
            -webkit-backdrop-filter: blur(4px) !important;
            background-color: rgba(15, 23, 42, 0.3) !important;
            opacity: 1 !important;
          }

          :root {
            --primary-color: #2563eb;
            --primary-hover: #1d4ed8;
            --primary-soft: #eff6ff;
            --primary-glow: rgba(37, 99, 235, 0.15);
            --secondary-color: #64748b;
            --secondary-hover: #475569;
            --border-color: #e2e8f0;
            --bg-light: #f8fafc;
            --text-main: #0f172a;
            --text-muted: #64748b;
            --radius-md: 12px;
            --radius-sm: 8px;
            --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          }

          @media (min-width: 992px) {
            .modal-lg {
              max-width: 1100px !important;
              width: 1100px !important;
            }
          }

          .modal-content {
            border-radius: 12px !important;
            border: none !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
            overflow: hidden !important;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
          }

          /* Match ToolsPopup header style */
          .modal-header {
            position: relative !important;
            display: block !important;
            padding: 24px 28px 20px 32px !important;
            background: linear-gradient(90deg, var(--primary-soft) 0%, rgba(255,255,255,0.98) 54%, #ffffff 100%) !important;
            border-bottom: 1px solid #edf2f7 !important;
            overflow: hidden !important;
          }

          .modal-header::before {
            content: "" !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            bottom: 0 !important;
            width: 4px !important;
            background: var(--primary-color) !important;
          }

          .rt-header-shell {
            display: flex !important;
            flex-direction: column !important;
            gap: 14px !important;
            width: 100% !important;
          }

          .rt-header-top {
            display: flex !important;
            justify-content: space-between !important;
            align-items: flex-start !important;
            gap: 18px !important;
            width: 100% !important;
          }

          .rt-header-main {
            display: flex !important;
            align-items: center !important;
            gap: 14px !important;
            min-width: 0 !important;
            flex: 1 1 auto !important;
          }

          .rt-header-icon {
            width: 52px !important;
            height: 52px !important;
            min-height: 52px !important;
            border-radius: 10px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            color: var(--primary-color) !important;
            background: #ffffff !important;
            border: 1px solid var(--primary-color) !important;
            box-shadow: 0 8px 18px -12px rgba(15,23,42,0.28) !important;
            font-size: 15px !important;
            flex-shrink: 0 !important;
          }

          .rt-header-copy {
            display: flex !important;
            flex-direction: column !important;
            gap: 8px !important;
            min-width: 0 !important;
            padding-top: 4px !important;
          }

          .rt-modal-title {
            display: inline-flex !important;
            align-items: center !important;
            margin: 0 !important;
            color: #0f172a !important;
            font-size: 18px !important;
            font-weight: 850 !important;
            line-height: 1.2 !important;
            min-width: 0 !important;
          }

          .rt-modal-title-text {
            display: inline-flex !important;
            align-items: center !important;
            gap: 12px !important;
            min-width: 0 !important;
            flex-wrap: wrap !important;
          }

          .rt-modal-title-badge {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 4px 10px !important;
            border-radius: 999px !important;
            font-size: 10px !important;
            font-weight: 800 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.02em !important;
            background: var(--primary-soft) !important;
            color: var(--primary-color) !important;
            border: 1px solid #e2e8f0 !important;
          }

          .rt-modal-meta-row {
            display: flex !important;
            align-items: center !important;
            flex-wrap: wrap !important;
            gap: 10px 12px !important;
            margin-top: 0 !important;
            padding-left: 0 !important;
            font-size: 12.5px !important;
            color: #64748b !important;
          }

          .rt-meta-desc {
            display: inline-flex !important;
            align-items: center !important;
            gap: 6px !important;
            color: #64748b !important;
            font-weight: 500 !important;
            line-height: 1.4 !important;
          }

          .rt-meta-desc i {
            color: var(--primary-color) !important;
          }

          /* Close button style matching ToolsPopup */
          .rt-action-btn {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 40px !important;
            height: 40px !important;
            padding: 0 !important;
            font-size: 12px !important;
            font-weight: 700 !important;
            color: #64748b !important;
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            cursor: pointer !important;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
            box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05) !important;
            outline: none !important;
          }

          .rt-action-btn:hover {
            color: #0f172a !important;
            border-color: #94a3b8 !important;
            background: #f8fafc !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
          }

          .rt-action-btn:active {
            transform: translateY(0) !important;
          }

          .rt-action-btn i {
            font-size: 11px !important;
            color: #94a3b8 !important;
            transition: color 0.2s ease !important;
          }

          .rt-action-btn:hover i {
            color: #ef4444 !important;
          }

          .modal-body {
            padding: 24px !important;
            background: #ffffff !important;
          }

          .modal-footer {
            border-top: 1px solid var(--border-color) !important;
            padding: 16px 24px !important;
            background: var(--bg-light) !important;
            display: flex !important;
            justify-content: flex-end !important;
            gap: 12px !important;
          }

          /* React Big Calendar Overrides */
          .rbc-calendar {
            font-family: inherit !important;
          }

          /* Button Spacing Fix */
          .rbc-btn-group {
            display: inline-flex !important;
            gap: 8px !important;
          }

          .rbc-btn-group button {
            color: var(--secondary-color) !important;
            border: 1.5px solid var(--border-color) !important;
            background: #ffffff !important;
            padding: 5px 14px !important;
            border-radius: 8px !important;
            font-weight: 700 !important;
            font-size: 13px !important;
            transition: all 0.2s ease !important;
            outline: none !important;
            margin: 0 !important;
          }

          .rbc-btn-group button:hover {
            background: var(--bg-light) !important;
            color: var(--text-main) !important;
            border-color: var(--secondary-hover) !important;
          }

          .rbc-btn-group button:active, .rbc-btn-group button.rbc-active {
            background: var(--primary-color) !important;
            color: #ffffff !important;
            border-color: var(--primary-color) !important;
            box-shadow: 0 2px 4px var(--primary-glow) !important;
          }

          .rbc-toolbar-label {
            font-weight: 700 !important;
            font-size: 14.5px !important;
            color: var(--text-main) !important;
          }

          .rbc-header {
            font-size: 10.5px !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.05em !important;
            color: var(--text-muted) !important;
            padding: 6px 0 !important;
            border-bottom: 2px solid var(--border-color) !important;
          }

          .rbc-month-view {
            border: 1.5px solid var(--border-color) !important;
            border-radius: 12px !important;
            overflow: hidden !important;
            background: #ffffff !important;
          }

          .rbc-day-bg {
            border-left: 1px solid #f1f5f9 !important;
            transition: all 0.2s ease !important;
          }

          .rbc-month-row {
            border-top: 1px solid #f1f5f9 !important;
          }

          .rbc-day-bg:hover {
            background-color: #f8fafc !important;
            cursor: pointer !important;
          }

          /* Clean rounded highlight states for date cells */
          .bg-selected-day {
            background-color: var(--primary-soft) !important;
            border: 2.5px solid var(--primary-color) !important;
            border-radius: 8px !important;
            box-shadow: inset 0 0 0 2px #ffffff, 0 4px 12px rgba(37, 99, 235, 0.15) !important;
          }

          .rbc-today {
            background-color: #fef08a !important;
            opacity: 0.8;
          }

          .bg-has-slots {
            background-color: rgba(37, 99, 235, 0.04) !important;
            border: 1.5px dashed rgba(37, 99, 235, 0.4) !important;
            border-radius: 8px !important;
          }

          .calendar-col-title {
            font-size: 12.5px;
            font-weight: 700;
            color: var(--text-muted);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-bottom: 10px;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .calendar-col-title i {
            color: var(--primary-color);
          }

          /* Time Slots Grid */
          .time-slots-grid {
            display: grid !important;
            grid-template-columns: repeat(auto-fill, minmax(95px, 1fr)) !important;
            gap: 8px !important;
            margin-top: 6px !important;
            padding: 2px !important;
          }

          .time-slot-pill {
            background: #ffffff !important;
            border: 1.5px solid var(--border-color) !important;
            color: var(--text-main) !important;
            border-radius: 20px !important;
            padding: 6px 10px !important;
            font-size: 12.5px !important;
            font-weight: 700 !important;
            text-align: center !important;
            cursor: pointer !important;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 5px !important;
            user-select: none !important;
          }

          .time-slot-pill:hover {
            border-color: var(--primary-color) !important;
            background: var(--primary-soft) !important;
            color: var(--primary-color) !important;
          }

          .time-slot-pill.is-selected {
            background: var(--primary-color) !important;
            border-color: var(--primary-color) !important;
            color: #ffffff !important;
            box-shadow: 0 3px 8px -2px rgba(37, 99, 235, 0.2) !important;
          }

          /* Custom slot builder styling */
          .custom-slot-builder {
            margin-top: 14px !important;
            padding-top: 12px !important;
            border-top: 1px dashed var(--border-color) !important;
          }

          .custom-slot-builder-title {
            font-size: 11px;
            font-weight: 700;
            color: var(--text-muted);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .custom-slot-builder-title i {
            color: var(--primary-color);
          }

          .custom-slot-builder-fields {
            display: flex !important;
            align-items: flex-end !important;
            gap: 10px !important;
          }

          .custom-time-input-group {
            display: flex !important;
            flex-direction: column !important;
            gap: 3px !important;
            flex: 1 !important;
          }

          .custom-time-input-group label {
            font-size: 10px !important;
            font-weight: 700 !important;
            color: var(--text-muted) !important;
            text-transform: uppercase !important;
            margin-bottom: 0 !important;
          }

          .custom-time-input-group input {
            border: 1.5px solid var(--border-color) !important;
            border-radius: 8px !important;
            padding: 5px 8px !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            color: var(--text-main) !important;
            outline: none !important;
            transition: border-color 0.2s ease !important;
            background: #ffffff !important;
            width: 100% !important;
          }

          .custom-time-input-group input:focus {
            border-color: var(--primary-color) !important;
          }

          .wf-btn-add-custom {
            background: var(--primary-soft) !important;
            color: var(--primary-color) !important;
            border: 1.5px solid var(--primary-soft) !important;
            padding: 6px 14px !important;
            border-radius: 8px !important;
            font-size: 12px !important;
            font-weight: 700 !important;
            cursor: pointer !important;
            transition: all 0.2s ease !important;
            white-space: nowrap !important;
            outline: none !important;
          }

          .wf-btn-add-custom:hover {
            background: var(--primary-color) !important;
            color: #ffffff !important;
            border-color: var(--primary-color) !important;
          }

          .custom-error-text {
            color: #ef4444 !important;
            font-size: 11.5px !important;
            font-weight: 600 !important;
            margin-top: 6px !important;
          }

          /* Selected tags style */
          .selected-slots-section {
            margin-top: 12px !important;
            background: var(--bg-light) !important;
            border: 1px solid var(--border-color) !important;
            border-radius: 8px !important;
            padding: 8px 12px !important;
          }

          .selected-slots-header {
            font-size: 10.5px !important;
            font-weight: 700 !important;
            color: var(--text-muted) !important;
            text-transform: uppercase !important;
            margin-bottom: 6px !important;
          }

          .selected-slots-tags {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 6px !important;
          }

          .selected-slot-tag {
            background: #ffffff !important;
            border: 1px solid var(--border-color) !important;
            color: var(--text-main) !important;
            border-radius: 6px !important;
            padding: 3px 8px !important;
            font-size: 11.5px !important;
            font-weight: 700 !important;
            display: inline-flex !important;
            align-items: center !important;
            gap: 5px !important;
            box-shadow: var(--shadow-sm) !important;
          }

          .remove-slot-tag-btn {
            border: none !important;
            background: transparent !important;
            color: var(--text-muted) !important;
            font-size: 14px !important;
            cursor: pointer !important;
            padding: 0 !important;
            line-height: 1 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
          }

          .remove-slot-tag-btn:hover {
            color: #ef4444 !important;
          }

          /* Buttons */
          .wf-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 8px 20px;
            font-size: 14px;
            font-weight: 700;
            border-radius: var(--radius-sm);
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            border: 1.5px solid transparent;
            min-width: 110px;
            outline: none !important;
          }

          .wf-btn-primary {
            background: var(--primary-color);
            color: #ffffff;
            border-color: var(--primary-color);
          }

          .wf-btn-primary:hover {
            background: var(--primary-hover);
            border-color: var(--primary-hover);
            transform: translateY(-1.5px);
            box-shadow: 0 8px 20px -4px rgba(37, 99, 235, 0.35);
          }

          .wf-btn-secondary {
            background: #fff;
            color: var(--secondary-color);
            border-color: var(--border-color);
          }

          .wf-btn-secondary:hover {
            background: var(--bg-light);
            color: var(--text-main);
            border-color: var(--secondary-hover);
            transform: translateY(-1.5px);
          }
        `}</style>
        <Modal.Header>
          <div className="rt-header-shell">
            <div className="rt-header-top">
              <div className="rt-header-main">
                <i className="rt-header-icon fa fa-calendar-plus-o" aria-hidden="true" />
                <div className="rt-header-copy">
                  <h2 className="rt-modal-title">
                    <span className="rt-modal-title-text">
                      <span>Add Meeting Slots</span>
                      <span className="rt-modal-title-badge">Scheduling Tool</span>
                    </span>
                  </h2>
                  <div className="rt-modal-meta-row">
                    <span className="rt-meta-desc">
                      <i className="fa fa-info-circle" aria-hidden="true" /> Propose one or more dates and times for the applicant to choose.
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="rt-action-btn"
                onClick={handleSecShowHide}
                title="Close"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6" style={{ height: "480px", display: "flex", flexDirection: "column" }}>
              <div className="calendar-col-title">
                <i className="fa fa-calendar" /> 1. Select Date
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <Calendar
                  selectable
                  localizer={localizer}
                  events={[]}
                  defaultView={Views.MONTH}
                  date={selectedDate}
                  views={["month"]}
                  onSelectSlot={handleSelectDate}
                  onNavigate={handleDateChanged}
                  dayPropGetter={(date) => {
                    const isSelectedDate = moment(date).isSame(selectedDate, "day");
                    const hasSlots = events.some((e) => moment(e.start).isSame(date, "day"));
                    
                    let classes = [];
                    if (isSelectedDate) {
                      classes.push("bg-selected-day");
                    } else if (hasSlots) {
                      classes.push("bg-has-slots");
                    }
                    
                    return { className: classes.join(" ") };
                  }}
                />
              </div>
            </div>
            <div className="col-md-6" style={{ height: "480px", display: "flex", flexDirection: "column" }}>
              <div className="calendar-col-title">
                <i className="fa fa-clock-o" /> 2. Pick Time Slots ({formattedSelectedDate})
              </div>
              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <div className="time-slots-grid">
                  {defaultTimeSlots.map((slot, index) => {
                    const active = isSelected(slot);
                    return (
                      <div
                        key={index}
                        className={`time-slot-pill ${active ? "is-selected" : ""}`}
                        onClick={() => handleToggleSlot(slot)}
                      >
                        {active && <i className="fa fa-check" />}
                        {slot.label}
                      </div>
                    );
                  })}
                </div>

                <div className="custom-slot-builder">
                  <div className="custom-slot-builder-title">
                    <i className="fa fa-sliders" /> Custom Time Range
                  </div>
                  <div className="custom-slot-builder-fields">
                    <div className="custom-time-input-group">
                      <label>Start</label>
                      <input
                        type="time"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                      />
                    </div>
                    <div className="custom-time-input-group">
                      <label>End</label>
                      <input
                        type="time"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="wf-btn-add-custom"
                      onClick={handleAddCustomSlot}
                    >
                      <i className="fa fa-plus" /> Add Range
                    </button>
                  </div>
                  {customError && <div className="custom-error-text">{customError}</div>}
                </div>

                {getSelectedSlotsForDate().length > 0 && (
                  <div className="selected-slots-section">
                    <div className="selected-slots-header">Selected for this day:</div>
                    <div className="selected-slots-tags">
                      {getSelectedSlotsForDate().map((e) => (
                        <span key={e.optionId} className="selected-slot-tag">
                          <i className="fa fa-clock-o" />
                          {formatTime(e.start)} - {formatTime(e.end)}
                          <button
                            type="button"
                            className="remove-slot-tag-btn"
                            onClick={() => {
                              const updated = events.filter((x) => x.optionId !== e.optionId);
                              setEvents(updated);
                            }}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="wf-btn wf-btn-secondary" onClick={handleSecShowHide} type="button">
            <i className="fa fa-times" aria-hidden="true"></i> Close
          </button>
          <button className="wf-btn wf-btn-primary" onClick={doSubmit} type="button">
            <i className="fa fa-check" aria-hidden="true"></i> Save Slots
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddSechduleTime;
